import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Col, Row} from "reactstrap";
import Button from "../../../../base/button/button";
import SearchInput from "../../../../base/input/search_input";
import IconButton from "../../../../base/button/icon_button";
import {ArrowBack, Star} from "@mui/icons-material";
import classnames from "classnames";
import {CustomerPaymentMethodTypes} from "../../../../../../core/constants/enums";
import {getCardMonth, getCardNumber, numComparator, stringComparator} from "../../../../../../core/services/utils/utils";
import {DataGridColumnAlignments, DataGridColumnPinnedTypes, DataGridColumnTypes, useDataGridSavedState} from "../../../../../../packages/data-grid";
import PartnerDataGrid from "../../../../../containers/data-grid";
import Api from "../../../../../../core/services/api/api";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import {PayCustomerOnlinePaymentCardSelectionDataGridColumnNames} from "../../../../../../core/constants/table/data-grid-column-keys";
import {Box, Typography} from "@mui/material";
import NewCreditCardForm from "../../../new-credit-card-form";


const cardTypeNames = {
    [CustomerPaymentMethodTypes.americanExpress]: 'American Express',
    [CustomerPaymentMethodTypes.masterCard]: 'Master Card',
    [CustomerPaymentMethodTypes.visa]: 'Visa',
    [CustomerPaymentMethodTypes.novusDiscover]: 'Novus discover',
    [CustomerPaymentMethodTypes.debit]: 'Debit',
    [CustomerPaymentMethodTypes.jcb]: 'Jcb',
    [CustomerPaymentMethodTypes.sears]: 'Sears',
}

const dataGridSaveConfig = {
    name: 'PayCustomerOrderOnlinePaymentCardSelectionSectionDataGrid',
    version: 2,
}

const PayCustomerOrderOnlinePaymentCardSelectionSection = ({
                                                               canGoBack,
                                                               goBack,
                                                               onCreditCardSelected,
                                                               customerId,
                                                               providers,
                                                           }) => {
    const formRef = useRef();
    const [submitting, setSubmitting] = useState(false);
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [keywords, setKeywords] = useState('');
    const isMounted = useIsMounted();

    const columns = useMemo(() =>
        /**@type {DataGridColumn[]}*/
        [
            {
                name: PayCustomerOnlinePaymentCardSelectionDataGridColumnNames.default,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                width: 0,
                resizable: false,
                sortable: false,
            },
            {
                title: 'Provider',
                name: PayCustomerOnlinePaymentCardSelectionDataGridColumnNames.provider,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
            },
            {
                title: 'Type',
                name: PayCustomerOnlinePaymentCardSelectionDataGridColumnNames.type,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
            },
            {
                title: 'Card number',
                name: PayCustomerOnlinePaymentCardSelectionDataGridColumnNames.cardNumber,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                format: getCardNumber,
                width: 140,
            },
            {
                title: 'Expiry',
                name: PayCustomerOnlinePaymentCardSelectionDataGridColumnNames.expiryDate,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                format: getCardMonth,
                width: 60,
            },
            {
                name: PayCustomerOnlinePaymentCardSelectionDataGridColumnNames.select,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                sortable: false,
                pinned: true,
                pinnedToggleable: false,
                pinnedType: DataGridColumnPinnedTypes.right,
                width: 70,
                resizable: false,
            },
        ], [])

    const [dataGridColumns, dataGridSortBy, , dataGridDensity] = useDataGridSavedState({
        columns: columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
        sortByObjectMap: {
            property: 'field',
            descending: 'descending',
        },
    });
    const [orderBy, setOrderBy] = useState(dataGridSortBy);

    /**@type {any[]}*/
    const visibleCards = useMemo(() => {
        let _keywords = keywords?.trim().toLowerCase() ?? '';
        return data?.filter(card =>
            cardTypeNames[card.type]?.toLowerCase()?.includes(_keywords) ||
            card.lastFourDigits?.toLowerCase()?.includes(_keywords) ||
            card.month?.toLowerCase()?.includes(_keywords) ||
            card.providerName?.toLowerCase()?.includes(_keywords)
        ) ?? []
    }, [data, keywords])

    /**@type {function(a, b): number}*/
    const orderByMethod = useMemo(() => {
        switch (orderBy?.field) {
            case PayCustomerOnlinePaymentCardSelectionDataGridColumnNames.type:
                return (a, b) => orderBy.descending
                    ? stringComparator(a?.type ?? '', b?.type ?? '')
                    : stringComparator(b?.type ?? '', a?.type ?? '')
            case PayCustomerOnlinePaymentCardSelectionDataGridColumnNames.cardNumber:
                return (a, b) => orderBy.descending
                    ? stringComparator(a?.lastFourDigits ?? '', b?.lastFourDigits ?? '')
                    : stringComparator(b?.lastFourDigits ?? '', a?.lastFourDigits ?? '')
            case PayCustomerOnlinePaymentCardSelectionDataGridColumnNames.expiryDate:
                return (a, b) => orderBy.descending
                    ? numComparator(Number(a?.month ?? 0), Number(b?.month ?? 0))
                    : numComparator(Number(b?.month ?? 0), Number(a?.month ?? 0))
            default:
                return () => 0
        }
    }, [orderBy]);

    /**
     * Fetches the list of users credits cards from the server and if the result of the api is successful, sets the
     * state.
     */
    const getCreditCards = useCallback(async () => {
        setLoading(true);
        const response = await Api.getCustomerCreditCardsV2(customerId);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setData(response.data ?? []);
        }
        setLoading(false);
    }, [customerId, isMounted])

    /**
     * With each change in the [customerId] value of the props:
     * - fetches the credit cards of the given customerId.
     */
    useEffect(() => {
        if (!customerId) {
            return setData([]);
        }
        void getCreditCards();
    }, [customerId, getCreditCards])

    /**
     * Sets the selected Credit Card as the values of the given card with the id of -1 and then navigates to the
     * next step.
     *
     * @param {any} cardInfo            the created credit card or the card information
     * @param {boolean} alreadyCreated  weather the credit card is already been created for the user.
     * @param {boolean} shouldAdd       weather the credit card should be added to the local list.
     */
    const proceedWithOnlinePayment = useCallback((cardInfo, alreadyCreated = true, shouldAdd = false) => {
        if (alreadyCreated) {
            if (shouldAdd)
                setData(prevState => [...prevState, cardInfo]);
            onCreditCardSelected(cardInfo);
        } else {
            onCreditCardSelected({...cardInfo, id: -1});
        }
    }, [onCreditCardSelected])

    /**
     * Function to get the card type icon based on the card type.
     * @type {(function(*): *)|*}
     */
    const getCardType = useCallback((card) => {
        switch (card.type) {
            case CustomerPaymentMethodTypes.americanExpress:
            case CustomerPaymentMethodTypes.americanexpress:
            case CustomerPaymentMethodTypes.americanExpress2:
                return <div className='card-type--amex card-type--center' style={{height: "40px"}}/>
            case CustomerPaymentMethodTypes.debit:
                return <div className='card-type--jcb card-type--center' style={{height: "40px"}}/>
            case CustomerPaymentMethodTypes.masterCard:
            case CustomerPaymentMethodTypes.mastercard:
                return <div className='card-type--mastercard card-type--center' style={{height: "40px"}}/>
            case CustomerPaymentMethodTypes.novusDiscover:
            case CustomerPaymentMethodTypes.discover:
            case CustomerPaymentMethodTypes.discover2:
                return <div className='card-type--discover card-type--center' style={{height: "40px"}}/>
            case CustomerPaymentMethodTypes.jcb:
            case CustomerPaymentMethodTypes.jcb2:
                return <div className='card-type--jcb card-type--center' style={{height: "40px"}}/>
            case CustomerPaymentMethodTypes.visa:
                return <div className='card-type--visa card-type--center' style={{height: "40px"}}/>
            default:
                return <p className='default-card-type'>{card?.type ?? ''}</p>
        }
    }, [])

    /**@type {DataGridRow[]}*/
    const dataGridRows = useMemo(() => {
        return Array.from(visibleCards)
                ?.sort(orderByMethod)
                ?.map((card) => {
                    return {
                        key: card.id,
                        data: card,
                        cells: {
                            [PayCustomerOnlinePaymentCardSelectionDataGridColumnNames.provider]: card?.providerName,
                            [PayCustomerOnlinePaymentCardSelectionDataGridColumnNames.default]:
                                card?.isDefault && <Star color={'warning'}/>,
                            [PayCustomerOnlinePaymentCardSelectionDataGridColumnNames.type]: <>{getCardType(card)}</>,
                            [PayCustomerOnlinePaymentCardSelectionDataGridColumnNames.cardNumber]: card?.lastFourDigits,
                            [PayCustomerOnlinePaymentCardSelectionDataGridColumnNames.expiryDate]: card?.month,
                            [PayCustomerOnlinePaymentCardSelectionDataGridColumnNames.select]:
                                <Button
                                    disabled={loading}
                                    variant={'outlined'}
                                    color={'primary'}
                                    onClick={() => proceedWithOnlinePayment(card, true)}
                                >
                                    Select
                                </Button>,
                        }
                    }
                })
            ?? []
    }, [visibleCards, orderByMethod, getCardType, loading, proceedWithOnlinePayment])

    return (
        <>
            {
                canGoBack &&
                <Row className='mt-2 no-gutters w-100'>
                    <IconButton
                        onPressed={() => goBack()}
                        color={'secondary'}
                    >
                        <ArrowBack className={'icon normal'}/>
                    </IconButton>
                    <p className={classnames('text-md text-color-1 ml-2')}>
                        Please select your desired credit card or enter a new card information.
                    </p>
                </Row>
            }
            <Row className='mt-2 no-gutters w-100'>
                <Box className={'px-4'} sx={{display: 'flex', flexDirection: 'column', width: '100%'}}>
                    <Typography variant={'subtitle1'} fontWeight={600} marginBlockEnd={2}>
                        Add a new credit card and pay
                    </Typography>
                    <NewCreditCardForm
                        onSuccess={proceedWithOnlinePayment}
                        customerId={customerId}
                        setSubmitting={setSubmitting}
                        providers={providers}
                        providerRenderType={'buttons'}
                        className={'py-3'}
                        ref={formRef}
                    />
                </Box>
                <Col xs={12} className={`px-3 d-flex align-items-center justify-content-center mb-4`}>
                    <Button
                        disabled={!!submitting}
                        color={'primary'}
                        variant={'contained'}
                        className='py-2-5 px-6'
                        onClick={() => formRef.current?.submit(true)}
                    >
                        {
                            submitting
                                ? "Adding..."
                                : "Add and Pay"
                        }
                    </Button>
                </Col>
                <Box className={'px-4'} sx={{display: 'flex', flexDirection: 'column'}}>
                    <Typography variant={'subtitle1'} fontWeight={600} marginBlockEnd={2}>
                        Customer's Saved credit cards
                    </Typography>
                    <SearchInput
                        autoSearch
                        loading={loading}
                        search={setKeywords}
                        label={'Search'}
                        variant={'outlined'}
                        paperProps={{className: 'px-0',}}
                    />
                    <PartnerDataGrid
                        {...dataGridSaveConfig}
                        initialLayoutState={{
                            columns: columns,
                        }}
                        columns={dataGridColumns}
                        hideFooter
                        hideExporting
                        hideToolbar
                        classNames={{
                            container: "px-0",
                            toolbar: 'pt-0',
                        }}
                        rows={dataGridRows}
                        state={{
                            loading: {state: loading},
                            sortBy: orderBy,
                            density: dataGridDensity,
                        }}
                        onSortByChanged={(_, current) => setOrderBy(current)}
                    />
                </Box>
            </Row>
        </>
    )
}

export default PayCustomerOrderOnlinePaymentCardSelectionSection;
